import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import { IoCheckmarkCircleOutline, IoEllipseOutline, IoOpenOutline } from 'react-icons/io5';

const FIREBASE_CONSOLE_URL = 'https://console.firebase.google.com';
const GCP_ENABLE_API_URL =
  'https://console.cloud.google.com/apis/library/firebase.googleapis.com';

const GIS_SCOPE = 'https://www.googleapis.com/auth/cloud-platform';

function loadGsiScript() {
  return new Promise((resolve, reject) => {
    if (typeof window !== 'undefined' && window.google?.accounts?.oauth2) {
      resolve();
      return;
    }
    const existing = document.getElementById('google-gsi-client');
    if (existing && existing.dataset.loaded === '1') {
      resolve();
      return;
    }
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Sign-In')));
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.id = 'google-gsi-client';
    s.onload = () => {
      s.dataset.loaded = '1';
      resolve();
    };
    s.onerror = () => reject(new Error('Failed to load Google Sign-In script'));
    document.body.appendChild(s);
  });
}

export default function AdminFirebase() {
  const envGoogleId = (process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || '').trim();
  const [googleWebClientId, setGoogleWebClientId] = useState(envGoogleId);

  const [status, setStatus] = useState({ configured: false, projectId: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [gsiReady, setGsiReady] = useState(false);
  const [discoverBusy, setDiscoverBusy] = useState(false);
  const [discoverError, setDiscoverError] = useState('');
  const [accessToken, setAccessToken] = useState(null);

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [webApps, setWebApps] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState('');
  const [envLines, setEnvLines] = useState('');
  const [sdkSummary, setSdkSummary] = useState(null);

  useEffect(() => {
    api('/api/admin/firebase/status')
      .then(setStatus)
      .catch((e) => setError(e.message || 'Failed to load status'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (envGoogleId) return;
    api('/api/admin/firebase/oauth-web-client-id')
      .then((d) => {
        const id = (d?.clientId && String(d.clientId).trim()) || '';
        if (id) setGoogleWebClientId(id);
      })
      .catch(() => {});
  }, [envGoogleId]);

  useEffect(() => {
    if (!googleWebClientId) return;
    loadGsiScript()
      .then(() => setGsiReady(true))
      .catch((e) => setDiscoverError(e.message || 'Google Sign-In failed to load'));
  }, [googleWebClientId]);

  const fetchProjectsWithToken = useCallback(async (token) => {
    const data = await api('/api/admin/firebase/discover/projects', {
      method: 'POST',
      body: JSON.stringify({ accessToken: token }),
    });
    const list = data.projects || [];
    setProjects(list);
    setSelectedProjectId(list.length === 1 ? list[0].projectId : '');
    setWebApps([]);
    setSelectedAppId('');
    setEnvLines('');
    setSdkSummary(null);
  }, []);

  const handleConnectGoogle = () => {
    setDiscoverError('');
    if (!googleWebClientId) {
      setDiscoverError(
        'Add REACT_APP_GOOGLE_WEB_CLIENT_ID to your frontend .env (OAuth 2.0 Web client ID from Google Cloud Console → APIs & Services → Credentials). Authorized JavaScript origins must include this origin (e.g. http://localhost:3000). Restart npm start after saving.'
      );
      return;
    }
    if (!window.google?.accounts?.oauth2) {
      setDiscoverError('Google Sign-In script not ready yet. Refresh the page.');
      return;
    }
    setDiscoverBusy(true);
    try {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: googleWebClientId,
        scope: GIS_SCOPE,
        callback: async (resp) => {
          if (resp.error) {
            setDiscoverError(resp.error_description || resp.error || 'Google authorization failed');
            setDiscoverBusy(false);
            return;
          }
          const tok = resp.access_token;
          if (!tok) {
            setDiscoverError('No access token returned.');
            setDiscoverBusy(false);
            return;
          }
          setAccessToken(tok);
          try {
            await fetchProjectsWithToken(tok);
          } catch (e) {
            setDiscoverError(e.message || 'Could not list Firebase projects');
          } finally {
            setDiscoverBusy(false);
          }
        },
      });
      client.requestAccessToken({ prompt: '' });
    } catch (e) {
      setDiscoverError(e.message || 'Google Sign-In failed');
      setDiscoverBusy(false);
    }
  };

  const loadWebApps = async () => {
    if (!accessToken || !selectedProjectId) return;
    setDiscoverBusy(true);
    setDiscoverError('');
    try {
      const data = await api('/api/admin/firebase/discover/web-apps', {
        method: 'POST',
        body: JSON.stringify({ accessToken, projectId: selectedProjectId }),
      });
      const apps = data.webApps || [];
      setWebApps(apps);
      setSelectedAppId(apps.length === 1 ? apps[0].appId : '');
      setEnvLines('');
      setSdkSummary(null);
    } catch (e) {
      setDiscoverError(e.message || 'Could not list web apps');
    } finally {
      setDiscoverBusy(false);
    }
  };

  const fetchSdkConfig = async () => {
    if (!accessToken || !selectedAppId) return;
    setDiscoverBusy(true);
    setDiscoverError('');
    try {
      const data = await api('/api/admin/firebase/discover/sdk-config', {
        method: 'POST',
        body: JSON.stringify({ accessToken, appId: selectedAppId }),
      });
      setEnvLines(data.envLines || '');
      setSdkSummary(data.sdkConfig || null);
    } catch (e) {
      setDiscoverError(e.message || 'Could not fetch SDK config');
    } finally {
      setDiscoverBusy(false);
    }
  };

  const copyEnv = async () => {
    try {
      await navigator.clipboard.writeText(envLines);
    } catch (_) {
      setDiscoverError('Clipboard copy failed — select the text manually.');
    }
  };

  const downloadEnvSnippet = () => {
    const blob = new Blob([envLines + '\n'], { type: 'text/plain;charset=utf-8' });
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = u;
    a.download = 'firebase-web-env-snippet.txt';
    a.click();
    URL.revokeObjectURL(u);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
        <CardHeader
          title="Firebase Auth (Customer login)"
          subheader="Social login (Google) and Phone OTP for storefront customers"
        />
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Alert
            severity={status.configured ? 'success' : 'info'}
            sx={{ mb: 2 }}
            action={
              status.configured ? null : (
                <Button
                  color="inherit"
                  size="small"
                  href={FIREBASE_CONSOLE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<IoOpenOutline size={18} />}
                >
                  Open Firebase Console
                </Button>
              )
            }
          >
            {status.configured
              ? `Backend is configured${status.projectId ? ` for project: ${status.projectId}` : ''}. Customers can sign in with Google or Phone OTP if the frontend has Firebase env vars set.`
              : 'Firebase is not configured. Follow the steps below to enable customer social login and OTP.'}
          </Alert>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Fetch web config from your Google account
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sign in with the Google account that owns your Firebase project. The server calls Google&apos;s Firebase Management API once per step; your access token is not stored. You still copy values into{' '}
            <code style={{ background: 'rgba(0,0,0,0.06)', padding: '0 4px', borderRadius: 4 }}>.env</code> and restart the React app.
          </Typography>

          {!googleWebClientId ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Set <strong>REACT_APP_GOOGLE_WEB_CLIENT_ID</strong> in your frontend environment (OAuth 2.0 Client ID of type &quot;Web application&quot; from{' '}
              <Button size="small" href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none', p: 0, minWidth: 0 }}>
                Google Cloud → Credentials
              </Button>
              ). Under Authorized JavaScript origins, add <code>http://localhost:3000</code> (and your production shop URL). Then restart <code>npm start</code>.
            </Alert>
          ) : (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              OAuth Web Client ID loaded.{gsiReady ? '' : ' Loading Google Sign-In…'}
            </Typography>
          )}

          <Button
            variant="contained"
            color="primary"
            disabled={discoverBusy || !googleWebClientId || !gsiReady}
            onClick={handleConnectGoogle}
            sx={{ mb: 2 }}
          >
            {discoverBusy ? <CircularProgress size={22} color="inherit" sx={{ mr: 1 }} /> : null}
            Connect Google account &amp; load projects
          </Button>

          {discoverError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDiscoverError('')}>
              {discoverError}
              {/Firebase Management API|SERVICE_DISABLED|403|503/i.test(discoverError) ? (
                <span>
                  {' '}
                  Enable the{' '}
                  <Button size="small" href={GCP_ENABLE_API_URL} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none', verticalAlign: 'baseline', p: 0, minWidth: 0 }}>
                    Firebase Management API
                  </Button>{' '}
                  for your Google Cloud project if prompted.
                </span>
              ) : null}
            </Alert>
          )}

          {projects.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="fb-project-label">Firebase project</InputLabel>
                <Select
                  labelId="fb-project-label"
                  label="Firebase project"
                  value={selectedProjectId}
                  onChange={(e) => {
                    setSelectedProjectId(e.target.value);
                    setWebApps([]);
                    setSelectedAppId('');
                    setEnvLines('');
                    setSdkSummary(null);
                  }}
                >
                  {projects.map((p) => (
                    <MenuItem key={p.projectId} value={p.projectId}>
                      {p.displayName} ({p.projectId})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" size="small" disabled={!selectedProjectId || discoverBusy} onClick={loadWebApps}>
                Load web apps in this project
              </Button>
            </Box>
          )}

          {webApps.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="fb-webapp-label">Web app</InputLabel>
                <Select
                  labelId="fb-webapp-label"
                  label="Web app"
                  value={selectedAppId}
                  onChange={(e) => {
                    setSelectedAppId(e.target.value);
                    setEnvLines('');
                    setSdkSummary(null);
                  }}
                >
                  {webApps.map((a) => (
                    <MenuItem key={a.appId} value={a.appId}>
                      {a.displayName} ({a.appId})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" size="small" disabled={!selectedAppId || discoverBusy} onClick={fetchSdkConfig}>
                Fetch frontend env snippet (SDK config)
              </Button>
            </Box>
          )}

          {envLines ? (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Paste into your frontend <code>.env</code> (then restart <code>npm start</code>)
              </Typography>
              <TextField
                multiline
                fullWidth
                minRows={8}
                value={envLines}
                InputProps={{ readOnly: true }}
                sx={{ mb: 1, '& textarea': { fontFamily: 'monospace', fontSize: 13 } }}
              />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Button size="small" variant="outlined" onClick={copyEnv}>
                  Copy to clipboard
                </Button>
                <Button size="small" variant="outlined" onClick={downloadEnvSnippet}>
                  Download .txt
                </Button>
              </Box>
              {sdkSummary?.projectId ? (
                <Typography variant="caption" color="text.secondary">
                  Project <strong>{sdkSummary.projectId}</strong> · App <strong>{sdkSummary.appId}</strong>
                </Typography>
              ) : null}
              <Alert severity="info" sx={{ mt: 2 }}>
                Backend token verification still needs a{' '}
                <strong>service account JSON</strong> in <code>backend/.env</code> (<code>FIREBASE_SERVICE_ACCOUNT_JSON</code> or <code>GOOGLE_APPLICATION_CREDENTIALS</code>). Download from Firebase Console → Project settings → Service accounts.
              </Alert>
            </Box>
          ) : null}

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: envLines ? 3 : 2 }}>
            Manual setup (if you prefer not to use Connect)
          </Typography>
          <List dense disablePadding>
            <ListItem alignItems="flex-start" sx={{ alignItems: 'center' }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {status.configured ? <IoCheckmarkCircleOutline size={22} color="green" /> : <IoEllipseOutline size={18} />}
              </ListItemIcon>
              <ListItemText
                primary="1. Open Firebase Console"
                secondary={
                  <>
                    Go to{' '}
                    <Button
                      size="small"
                      href={FIREBASE_CONSOLE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: 'none', p: 0, minWidth: 0, verticalAlign: 'baseline' }}
                    >
                      console.firebase.google.com
                    </Button>{' '}
                    and sign in with your Google account. Create a project (or select an existing one).
                  </>
                }
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ alignItems: 'center' }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {status.configured ? <IoCheckmarkCircleOutline size={22} color="green" /> : <IoEllipseOutline size={18} />}
              </ListItemIcon>
              <ListItemText
                primary="2. Enable Authentication"
                secondary="In the project: Build → Authentication → Get started. Enable Sign-in methods: Google and Phone (and optionally Email link for email OTP)."
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ alignItems: 'center' }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <IoEllipseOutline size={18} />
              </ListItemIcon>
              <ListItemText
                primary="3. Web app config (frontend)"
                secondary={
                  <>
                    Project settings (gear) → General → Your apps → Add app (Web). Copy the config object and add to your frontend{' '}
                    <code style={{ background: 'rgba(0,0,0,0.06)', padding: '0 4px', borderRadius: 4 }}>.env</code>: REACT_APP_FIREBASE_API_KEY, REACT_APP_FIREBASE_AUTH_DOMAIN, REACT_APP_FIREBASE_PROJECT_ID, REACT_APP_FIREBASE_APP_ID (and optionally STORAGE_BUCKET, MESSAGING_SENDER_ID). Or use <strong>Connect Google account</strong> above.
                  </>
                }
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ alignItems: 'center' }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <IoEllipseOutline size={18} />
              </ListItemIcon>
              <ListItemText
                primary="4. Service account (backend)"
                secondary={
                  <>
                    Project settings → Service accounts → Generate new private key. Add to backend{' '}
                    <code style={{ background: 'rgba(0,0,0,0.06)', padding: '0 4px', borderRadius: 4 }}>.env</code> as FIREBASE_SERVICE_ACCOUNT_JSON (paste the whole JSON as one line, no newlines) or set GOOGLE_APPLICATION_CREDENTIALS to the path of the downloaded JSON file.
                  </>
                }
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ alignItems: 'center' }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <IoEllipseOutline size={18} />
              </ListItemIcon>
              <ListItemText
                primary="5. Restart"
                secondary="Restart the backend server and the React app so env vars are picked up. Customer login page will then show Google and Phone OTP options."
              />
            </ListItem>
          </List>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            After setup, customers use the same “My Account” / Login page: they can sign in with email/password or with Google / Phone OTP. The backend verifies Firebase ID tokens and issues your existing customer JWT.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
